import React, { useMemo } from "react";
import { Alert, Col, Row, Spinner } from "react-bootstrap";

import SummaryBoxCard from "./SummaryBoxCard";
import { getAllHitsSummary } from "../../../services/finance/payments-service.js";
import { useQuery } from "@tanstack/react-query";
import { formatNumber } from "../../../utils/helper_functions";
import { DASHBOARD_REFETCH_INTERVAL_MS } from "./constants";

function calculatePercentageChange(current, previous) {
  current = parseInt(current, 10);
  previous = parseInt(previous, 10);

  if (isNaN(current) || isNaN(previous)) {
    return null;
  }

  if (current === 0 && previous === 0) {
    return 0;
  }

  if (previous === 0) {
    return null;
  }

  return ((current - previous) / previous) * 100;
}

function SummaryBarWidget() {
  const { isLoading, isError, error, data } = useQuery(
    ["finance", "hits-summary"],
    getAllHitsSummary,
    {
      refetchInterval: DASHBOARD_REFETCH_INTERVAL_MS,
      refetchIntervalInBackground: false
    }
  );

  const summary = data?.data;

  const percentageChange = useMemo(() => {
    if (!summary) {
      return {};
    }
    return {
      totalHitsChange: calculatePercentageChange(summary.total_hits, summary.previous_total_hits),
      successHitsChange: calculatePercentageChange(summary.success_hits, summary.previous_success_hits),
      revenueChange: calculatePercentageChange(summary.revenue, summary.previous_revenue),
      failedHitsChange: calculatePercentageChange(summary.failed_hits, summary.previous_failed_hits)
    };
  }, [summary]);

  if (isLoading) {
    return <Spinner />;
  }

  if (isError) {
    return <Alert variant="danger">Error: {error.message}</Alert>;
  }

  return (
    <div>
      <Row className="gy-2">
        <Col lg={3} md={4} xs={6}>
          <SummaryBoxCard
            variant="info"
            label="Total Hits"
            value={formatNumber(summary?.total_hits)}
            PercentageChange={percentageChange.totalHitsChange?.toFixed(2)}
          />
        </Col>
        <Col lg={3} md={4} xs={6}>
          <SummaryBoxCard
            variant="success"
            label="Success"
            value={formatNumber(summary?.success_hits)}
            PercentageChange={percentageChange.successHitsChange?.toFixed(2)}
          />
        </Col>
        <Col lg={3} md={4} xs={6}>
          <SummaryBoxCard
            variant="danger"
            label="Failed"
            value={formatNumber(summary?.failed_hits)}
            PercentageChange={percentageChange.failedHitsChange?.toFixed(2)}
          />
        </Col>
        <Col lg={3} md={12} xs={6}>
          <SummaryBoxCard
            variant="primary"
            label="Revenue"
            value={formatNumber(summary?.revenue)}
            PercentageChange={percentageChange.revenueChange?.toFixed(2)}
          />
        </Col>
      </Row>
    </div>
  );
}

export default SummaryBarWidget;
